import { useState } from "react";
import { motion } from "framer-motion";

export default function CookiesPolicy() {
  const [openSections, setOpenSections] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const cookieCategories = [
    {
      id: "essential",
      name: "Essential Cookies",
      description: "Required for basic website functionality",
      color: "from-green-500 to-green-600",
      icon: "🔐",
      necessary: true
    },
    {
      id: "performance",
      name: "Performance Cookies",
      description: "Help us understand how visitors interact",
      color: "from-blue-500 to-blue-600",
      icon: "📊",
      necessary: false
    },
    {
      id: "functional",
      name: "Functional Cookies",
      description: "Remember your preferences and settings",
      color: "from-purple-500 to-purple-600",
      icon: "⚙️",
      necessary: false
    },
    {
      id: "targeting",
      name: "Targeting Cookies",
      description: "Used for advertising and marketing",
      color: "from-orange-500 to-orange-600",
      icon: "🎯",
      necessary: false
    }
  ];

  const cookiesList = [
    {
      name: "session_id",
      category: "essential",
      duration: "Session",
      purpose: "Maintain your logged-in state",
      provider: "Techspear Ventures"
    },
    {
      name: "user_preferences",
      category: "functional",
      duration: "1 Year",
      purpose: "Store your language and theme preferences",
      provider: "Techspear Ventures"
    },
    {
      name: "_ga",
      category: "performance",
      duration: "2 Years",
      purpose: "Google Analytics - distinguish users",
      provider: "Google"
    },
    {
      name: "_gid",
      category: "performance",
      duration: "24 Hours",
      purpose: "Google Analytics - distinguish users",
      provider: "Google"
    },
    {
      name: "_fbp",
      category: "targeting",
      duration: "3 Months",
      purpose: "Facebook Pixel - ad delivery",
      provider: "Facebook"
    },
    {
      name: "ad_user_id",
      category: "targeting",
      duration: "1 Year",
      purpose: "Personalized advertising",
      provider: "Techspear Ventures"
    }
  ];

  const filteredCookies = activeCategory === "all" 
    ? cookiesList 
    : cookiesList.filter(cookie => cookie.category === activeCategory);

  const sections = [
    {
      id: "what-are-cookies",
      title: "What Are Cookies?",
      icon: "🍪",
      content: `Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us provide you with a better experience by remembering your preferences, understanding how you use our site, and showing you relevant content.`
    },
    {
      id: "how-we-use",
      title: "How We Use Cookies",
      icon: "🎯",
      content: `We use cookies for several purposes:

• Essential Operation: To keep you logged in and maintain your session
• Performance Tracking: To understand how visitors use our website
• Personalization: To remember your preferences and settings
• Marketing: To show you relevant advertisements and content
• Analytics: To improve our website based on user behavior`
    },
    {
      id: "cookie-types",
      title: "Types of Cookies We Use",
      icon: "📋",
      content: `We use different types of cookies for various purposes:

Essential Cookies: Required for basic website functionality. These cannot be disabled.
Performance Cookies: Help us understand how visitors interact with our website.
Functional Cookies: Remember your preferences and settings.
Targeting Cookies: Used for advertising and marketing purposes.`
    },
    {
      id: "third-party",
      title: "Third-Party Cookies",
      icon: "🤝",
      content: `Some cookies on our website are placed by third-party services we use, such as:

• Google Analytics: For website analytics and performance tracking
• Social Media Platforms: For social sharing and integration
• Advertising Networks: For personalized advertising
• Payment Processors: For secure transaction processing

These third parties have their own privacy policies and cookie practices.`
    },
    {
      id: "managing-cookies",
      title: "Managing Your Cookie Preferences",
      icon: "⚙️",
      content: `You have control over cookies. Here's how to manage them:

Browser Settings: Most browsers allow you to refuse or delete cookies. Check your browser's help section for instructions.
Opt-out Tools: Use industry opt-out tools for advertising cookies.
Our Preference Center: Use the cookie settings panel on our website to choose which types of cookies you accept.

Note: Blocking essential cookies may affect website functionality.`
    },
    {
      id: "duration",
      title: "Cookie Duration",
      icon: "⏰",
      content: `Cookies have different lifespans:

Session Cookies: Exist only during your browsing session and are deleted when you close your browser.
Persistent Cookies: Remain on your device for a set period (from days to years) unless you delete them.

We specify the duration of each cookie in our detailed cookie list.`
    },
    {
      id: "updates",
      title: "Policy Updates",
      icon: "🔄",
      content: `We may update this Cookies Policy from time to time to reflect changes in technology, regulation, or our services. We will notify you of significant changes by posting a notice on our website or through other communication channels.

We recommend reviewing this policy periodically to stay informed about how we use cookies.`
    },
    {
      id: "contact",
      title: "Contact & Questions",
      icon: "📞",
      content: `If you have any questions about our use of cookies or this policy, please contact us:

Email: privacy@techspearventures.com
Phone: 404-206-2653
Address: Techspear Ventures, 75 5th Street NW, Suite 427, Atlanta, Georgia 30308

We're committed to being transparent about our data practices.`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-16 font-sans tracking-wide">
      <div className="px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-6">
            🍪 Cookie Policy & Preferences
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-mokoto text-gray-900 mb-6">
            Cookies Policy
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-600">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Last Updated: July 10, 2020</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Your Privacy, Your Choice</span>
            </div>
          </div>
        </motion.div>

        {/* Cookie Categories Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {cookieCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 text-center">
              <div className={`w-16 h-16 bg-gradient-to-r ${category.color} rounded-2xl flex items-center justify-center text-white text-2xl mx-auto mb-4`}>
                {category.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.name}</h3>
              <p className="text-gray-600 text-sm mb-3">{category.description}</p>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                category.necessary 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {category.necessary ? 'Required' : 'Optional'}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Interactive Cookie Manager */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12"
        >
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cookie Settings */}
            <div className="lg:w-1/2">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cookie Preferences</h3>
              <div className="space-y-4">
                {cookieCategories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 bg-gradient-to-r ${category.color} rounded-xl flex items-center justify-center text-white`}>
                        {category.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{category.name}</h4>
                        <p className="text-gray-600 text-sm">{category.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {category.necessary ? (
                        <span className="text-sm text-gray-500">Always Active</span>
                      ) : (
                        <span className="text-sm text-gray-500">Optional</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="lg:w-1/2">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h3>
              <div className="grid grid-cols-1 gap-4">
                <button className="p-4 border border-gray-200 rounded-xl text-left hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                      🚫
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Reject All Optional</h4>
                      <p className="text-gray-600 text-sm">Only essential cookies will be used</p>
                    </div>
                  </div>
                </button>
                <button className="p-4 border border-gray-200 rounded-xl text-left hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                      ✅
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Accept All</h4>
                      <p className="text-gray-600 text-sm">All cookies will be enabled</p>
                    </div>
                  </div>
                </button>
                <button className="p-4 border border-gray-200 rounded-xl text-left hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                      🧹
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Clear All Cookies</h4>
                      <p className="text-gray-600 text-sm">Remove existing cookies from browser</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Detailed Cookie List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Detailed Cookie List</h3>
            <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  activeCategory === "all" 
                    ? "bg-blue-600 text-white" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All Cookies
              </button>
              {cookieCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    activeCategory === category.id 
                      ? "bg-blue-600 text-white" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 font-semibold text-gray-900">Cookie Name</th>
                  <th className="text-left py-4 font-semibold text-gray-900">Category</th>
                  <th className="text-left py-4 font-semibold text-gray-900">Duration</th>
                  <th className="text-left py-4 font-semibold text-gray-900">Purpose</th>
                  <th className="text-left py-4 font-semibold text-gray-900">Provider</th>
                </tr>
              </thead>
              <tbody>
                {filteredCookies.map((cookie, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 font-mono text-sm text-gray-900">{cookie.name}</td>
                    <td className="py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        cookie.category === 'essential' ? 'bg-green-100 text-green-800' :
                        cookie.category === 'performance' ? 'bg-blue-100 text-blue-800' :
                        cookie.category === 'functional' ? 'bg-purple-100 text-purple-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {cookie.category}
                      </span>
                    </td>
                    <td className="py-4 text-gray-600">{cookie.duration}</td>
                    <td className="py-4 text-gray-600">{cookie.purpose}</td>
                    <td className="py-4 text-gray-600">{cookie.provider}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-12"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Policy Navigation</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  toggleSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-left p-3 rounded-lg bg-gray-50 hover:bg-amber-50 hover:text-amber-700 transition-all duration-200 border border-transparent hover:border-amber-200"
              >
                <div className="text-lg mb-1">{section.icon}</div>
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">{section.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Click to {openSections[section.id] ? 'collapse' : 'expand'} details
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-6 h-6 text-gray-500 transform transition-transform duration-200 ${
                    openSections[section.id] ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <motion.div
                initial={false}
                animate={{ 
                  height: openSections[section.id] ? 'auto' : 0,
                  opacity: openSections[section.id] ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                    {section.content.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Browser Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-8 text-white mt-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center">Browser Cookie Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { browser: "Chrome", icon: "🔍", steps: "Settings → Privacy → Cookies" },
              { browser: "Firefox", icon: "🦊", steps: "Options → Privacy → Cookies" },
              { browser: "Safari", icon: "🧭", steps: "Preferences → Privacy" },
              { browser: "Edge", icon: "🌐", steps: "Settings → Cookies" }
            ].map((browser, index) => (
              <div key={index} className="text-center bg-white/10 rounded-xl p-4">
                <div className="text-2xl mb-2">{browser.icon}</div>
                <h3 className="font-semibold mb-2">{browser.browser}</h3>
                <p className="text-amber-100 text-sm">{browser.steps}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center mt-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Cookie Questions?</h2>
          <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
            We believe in transparency and choice when it comes to your data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:Info@techspearventures.com"
              className="bg-amber-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <span>📧</span>
              <span>Email Privacy Team</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}